import React, { useState } from 'react';
import { recordingsApi } from '../../api';
import { AlertTriangleIcon, DownloadIcon } from '../icons';

const STATUS_STYLE = {
  recording:  { color: 'var(--red)',    bg: 'var(--red-light)'    },
  processing: { color: 'var(--orange)', bg: 'var(--orange-light)' },
  completed:  { color: 'var(--green)',  bg: 'var(--green-light)'  },
  failed:     { color: '#7c3aed',       bg: '#f5f3ff'             },
  cancelled:  { color: 'var(--gray-500)', bg: 'var(--gray-100)'   },
  expired:    { color: 'var(--gray-400)', bg: 'var(--gray-100)'   },
};

export default function SessionsTable({ sessions, loading }) {
  const [search, setSearch] = useState('');
  const [resendingId, setResendingId] = useState(null);
  const [localSessions, setLocalSessions] = useState(sessions);

  React.useEffect(() => setLocalSessions(sessions), [sessions]);

  const filtered = localSessions.filter(s => {
    const recipients = s.recipient_emails || [s.player_email];
    return (
      recipients.some(e => e.toLowerCase().includes(search.toLowerCase())) ||
      String(s.court_number).includes(search)
    );
  });

  const fmtBytes = b => {
    if (!b) return '—';
    if (b < 1024 * 1024) return `${(b/1024).toFixed(0)} KB`;
    return `${(b/(1024*1024)).toFixed(1)} MB`;
  };

  const fmtDate = iso => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const handleResend = async (session) => {
    setResendingId(session.id);
    try {
      const { session: updated } = await recordingsApi.resendEmail(session.id);
      setLocalSessions(prev => prev.map(s => s.id === updated.id ? updated : s));
    } catch {
      // swallow — the row's status simply won't change; admin can retry again
    } finally {
      setResendingId(null);
    }
  };

  return (
    <div style={s.wrap}>
      {/* Toolbar */}
      <div style={s.toolbar}>
        <div style={s.searchWrap}>
          <span style={s.searchIcon}>⌕</span>
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search recipient email, court…"
            style={s.search}
          />
        </div>
        <div style={s.meta}>
          {filtered.length} session{filtered.length !== 1 ? 's' : ''}
        </div>
      </div>

      {loading ? (
        <div style={s.empty}>Loading sessions…</div>
      ) : filtered.length === 0 ? (
        <div style={s.empty}>
          {search ? 'No sessions match your search.' : 'No sessions recorded yet.'}
        </div>
      ) : (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                {['Court','Recipients','Status','Date','Duration','Size','Email','Download'].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(session => {
                const ss = STATUS_STYLE[session.status] || STATUS_STYLE.cancelled;
                const recipients = session.recipient_emails || [session.player_email];
                const failedCount = session.email_failure_count || 0;
                return (
                  <tr key={session.id} style={s.tr}>
                    <td style={s.td}>
                      <span style={s.courtBadge}>Court {session.court_number}</span>
                    </td>
                    <td style={{ ...s.td, fontWeight: 600, color: 'var(--gray-900)' }}>
                      <div>{recipients[0]}</div>
                      {recipients.length > 1 && (
                        <div style={{ fontSize: 12, color: 'var(--gray-400)', fontWeight: 500 }}>
                          +{recipients.length - 1} more
                        </div>
                      )}
                    </td>
                    <td style={s.td}>
                      <span style={{ ...s.badge, background: ss.bg, color: ss.color }}>
                        {session.status}
                      </span>
                    </td>
                    <td style={{ ...s.td, color: 'var(--gray-500)', fontSize: 13, whiteSpace: 'nowrap' }}>
                      {fmtDate(session.started_at)}
                    </td>
                    <td style={{ ...s.td, fontVariantNumeric: 'tabular-nums', color: 'var(--gray-700)' }}>
                      {session.duration_display || '—'}
                    </td>
                    <td style={{ ...s.td, color: 'var(--gray-500)', fontSize: 13 }}>
                      {fmtBytes(session.file_size_bytes)}
                    </td>
                    <td style={s.td}>
                      {session.email_all_sent ? (
                        <span style={s.emailSent}>✓ Sent ({recipients.length}/{recipients.length})</span>
                      ) : failedCount > 0 ? (
                        <button
                          onClick={() => handleResend(session)}
                          disabled={resendingId === session.id}
                          style={s.resendBtn}
                        >
                          {resendingId === session.id
                    ? 'Retrying…'
                    : <><AlertTriangleIcon size={13} style={{ marginRight: 5, verticalAlign: -2 }} />Retry ({failedCount} failed)</>}
                        </button>
                      ) : (
                        <span style={s.emailPending}>—</span>
                      )}
                    </td>
                    <td style={s.td}>
                      {session.download_url
                        ? <a href={session.download_url} download style={s.downloadBtn}><DownloadIcon size={13} style={{ marginRight: 5, verticalAlign: -2 }} />Download</a>
                        : <span style={{ color: 'var(--gray-300)', fontSize: 13 }}>—</span>
                      }
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const s = {
  wrap: {
    background: 'var(--white)', borderRadius: 'var(--radius)',
    border: '1px solid var(--gray-200)', overflow: 'hidden',
    boxShadow: 'var(--shadow-sm)',
  },
  toolbar: {
    padding: '16px 20px',
    borderBottom: '1px solid var(--gray-100)',
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', gap: 12,
  },
  searchWrap: {
    position: 'relative', display: 'flex', alignItems: 'center',
  },
  searchIcon: {
    position: 'absolute', left: 11,
    color: 'var(--gray-400)', fontSize: 18, pointerEvents: 'none',
  },
  search: {
    padding: '8px 12px 8px 34px',
    border: '1.5px solid var(--gray-200)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 14, outline: 'none',
    width: 280, fontFamily: 'var(--font)',
    color: 'var(--gray-900)',
    background: 'var(--gray-50)',
  },
  meta: { fontSize: 13, color: 'var(--gray-400)', fontWeight: 500 },
  empty: {
    padding: '64px', textAlign: 'center',
    color: 'var(--gray-400)', fontSize: 15,
  },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  th: {
    padding: '10px 16px', textAlign: 'left',
    background: 'var(--gray-50)', color: 'var(--gray-500)',
    fontSize: 11, fontWeight: 600, letterSpacing: '0.06em',
    textTransform: 'uppercase',
    borderBottom: '1px solid var(--gray-200)',
    whiteSpace: 'nowrap',
  },
  tr: { borderBottom: '1px solid var(--gray-100)' },
  td: { padding: '12px 16px', color: 'var(--gray-700)', verticalAlign: 'middle' },
  badge: {
    padding: '3px 9px', borderRadius: 50,
    fontSize: 12, fontWeight: 600,
  },
  courtBadge: {
    background: 'var(--blue-light)', color: 'var(--blue)',
    padding: '3px 10px', borderRadius: 50,
    fontSize: 12, fontWeight: 600,
  },
  emailSent: { color: 'var(--green)', fontSize: 13, fontWeight: 500 },
  emailPending: { color: 'var(--gray-300)', fontSize: 13 },
  resendBtn: {
    background: '#fef3c7', color: '#92400e',
    border: '1px solid #fde68a', borderRadius: 6,
    padding: '4px 10px', fontSize: 12, fontWeight: 600,
    cursor: 'pointer', whiteSpace: 'nowrap',
  },
  downloadBtn: {
    color: 'var(--blue)', fontWeight: 600, fontSize: 13,
    padding: '4px 10px', borderRadius: 6,
    background: 'var(--blue-light)',
    border: '1px solid rgba(26,107,255,0.2)',
    whiteSpace: 'nowrap', textDecoration: 'none',
    display: 'inline-block',
  },
};
